import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../contexts/PageTitleContext';
import SideTab from '../../components/Mypage/sideTab';
import MypageList, { shouldRenderRecord } from './mypageList';
import MypageDetail from './MypageDetail';
import ChangePwd from '../../components/Mypage/changePwd';
import EditInfo from '../../components/Mypage/editInfo';
import '../../css/Mypage/mypage.css';
import { ReactComponent as ArrowRight } from '../../assets/icons/svg/arrowLeft.svg';
import { useUser } from '../../contexts/UserContext';
import { apiGet } from '../../utils/api';

const STATUS_CODE = {
  CANCEL: '-1',
  RESERVED: '0',      // 결제 대기
  PARTIAL_PAID: '1',  // 부분 결제 완료
  PAID: '2',          // 결제 완료 (전액) / 티켓 발행
  TICKET: '2',        // 티켓 발행 (동일 코드)
  ON_BOARD: '3',      // 승선 완료
  EXTERNAL: '5',      // 연동 예약
};

function Mypage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const { user, loading } = useUser();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 651);
  const [editInfoData, setEditInfoData] = useState(null);
  const [reservationsByStatus, setReservationsByStatus] = useState({});
  const [reservationsLoadingMap, setReservationsLoadingMap] = useState({});
  const [reservationsErrorMap, setReservationsErrorMap] = useState({});

  const sideTabGroups = React.useMemo(() => {
    return [
      {
        groupTitle: '예약 내역',
        items: [
          {
            id: 'reservation-complete',
            name: '결제 대기',
            path: '/mypage/reservation-complete',
            component: MypageList,
            status: STATUS_CODE.RESERVED,
            statusLabelOverride: { '0': '결제 대기' },
          },
          {
            id: 'partial-payment',
            name: '부분 결제 완료',
            path: '/mypage/partial-payment',
            component: MypageList,
            status: STATUS_CODE.PARTIAL_PAID,
          },
          {
            id: 'payment-complete',
            name: '결제 완료',
            path: '/mypage/payment-complete',
            component: MypageList,
            status: STATUS_CODE.PAID,
            ticketIssueFilter: 'not_ready',
            statusLabelOverride: { '2': '결제 완료' },
            buttonLabelOverride: { '2': '자세히 보기' },
          },
          {
            id: 'ticket-issue',
            name: '티켓 발행',
            path: '/mypage/ticket-issue',
            component: MypageList,
            status: STATUS_CODE.TICKET,
            ticketIssueFilter: 'ready',
            buttonLabelOverride: { '2': '자세히 보기' },
          },
          {
            id: 'boarding-complete',
            name: '승선 완료',
            path: '/mypage/boarding-complete',
            component: MypageList,
            // status=3(승선완료) + status=2 중 여행 종료일이 지난 건을 함께 노출
            status: '2,3',
            ticketIssueFilter: 'on_board',
            statusLabelOverride: { '2': '승선 완료', '3': '승선 완료' },
            buttonLabelOverride: { '2': '자세히 보기', '3': '자세히 보기' },
          },
          {
            id: 'external-reservation',
            name: '연동 예약',
            path: '/mypage/external-reservation',
            component: MypageList,
            status: STATUS_CODE.EXTERNAL,
          },
          {
            id: 'cancellation-history',
            name: '취소 내역',
            path: '/mypage/cancellation-history',
            component: MypageList,
            status: STATUS_CODE.CANCEL,
          },
        ],
      },
      {
        groupTitle: '회원정보',
        items: [
          { id: 'edit-profile', name: '회원정보 및 수정', path: '/mypage/edit-profile', component: EditInfo, data: null },
          { id: 'change-password', name: '비밀번호 변경', path: '/mypage/change-password', component: ChangePwd, data: null },
        ],
      },
    ];
  }, []);

  const allTabItems = React.useMemo(
    () => sideTabGroups.flatMap(group => group.items),
    [sideTabGroups]
  );
  const reservationStatusKeys = React.useMemo(() => {
    return Array.from(new Set(
      allTabItems
        .filter(item => item.component === MypageList && item.status != null)
        .map(item => String(item.status))
    ));
  }, [allTabItems]);

  const getTabRenderableCount = React.useCallback((tab) => {
    if (!tab || tab.component !== MypageList || tab.status == null) return 0;
    const statusKey = String(tab.status);
    const records = reservationsByStatus[statusKey] || [];
    if (!Array.isArray(records)) return 0;
    return records.filter(record => shouldRenderRecord(record, tab.ticketIssueFilter)).length;
  }, [reservationsByStatus]);

  const sideTabGroupsWithCounts = React.useMemo(() => {
    return sideTabGroups.map(group => ({
      ...group,
      items: group.items.map(item => ({
        ...item,
        count: getTabRenderableCount(item),
      })),
    }));
  }, [sideTabGroups, getTabRenderableCount]);

  const isDetailPage = location.pathname.includes('/detail');

  const currentActiveTab = allTabItems.find(item => 
    isDetailPage ? location.pathname.startsWith(item.path) : item.path === location.pathname
  );

  const isMypageBaseRoute = location.pathname === '/mypage' || location.pathname === '/mypage/';

  // 모바일 여부 체크
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 651);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 페이지 타이틀 설정
  useEffect(() => {
    if (isMypageBaseRoute) setPageTitle('');
    else if (currentActiveTab) setPageTitle(currentActiveTab.name);
  }, [currentActiveTab, isMypageBaseRoute, setPageTitle]);

  // 로그인 여부 확인 후 리디렉트
  useEffect(() => {
    if (!loading && !user) {
      const redirectUrl = location.pathname + location.search;
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
    }
  }, [loading, user, navigate, location.pathname, location.search]);

  const loadReservationsByStatus = React.useCallback((statusValue) => {
    if (!user) return undefined;
    const key = statusValue != null ? String(statusValue) : 'all';
    let isMounted = true;
    setReservationsLoadingMap(prev => ({ ...prev, [key]: true }));
    setReservationsErrorMap(prev => ({ ...prev, [key]: null }));

    const params = {};
    if (statusValue != null) params.status = statusValue;

    apiGet('/reservation/get_reservation_list.php', params)
      .then(res => {
        if (!isMounted) return;
        const payload = res?.data?.list ?? res?.data ?? res?.list ?? res;
        const nextReservations = Array.isArray(payload)
          ? payload.filter(item => Number(item?.status) !== -2)
          : [];
        setReservationsByStatus(prev => ({ ...prev, [key]: nextReservations }));
      })
      .catch(error => {
        if (!isMounted) return;
        setReservationsByStatus(prev => ({ ...prev, [key]: [] }));
        setReservationsErrorMap(prev => ({ ...prev, [key]: error }));
      })
      .finally(() => {
        if (!isMounted) return;
        setReservationsLoadingMap(prev => ({ ...prev, [key]: false }));
      });

    return () => { isMounted = false; };
  }, [user]);

  // 예약 내역 탭 카운트 표시를 위해 각 상태의 목록을 미리 로드
  useEffect(() => {
    if (!user) return;
    const cleanups = reservationStatusKeys
      .map(statusValue => loadReservationsByStatus(statusValue))
      .filter(Boolean);
    return () => {
      cleanups.forEach(cleanup => cleanup());
    };
  }, [user, reservationStatusKeys, loadReservationsByStatus]);

  // 상세에서 상태 동기화 후 돌아올 때 목록을 최신화
  useEffect(() => {
    if (location.state?.refreshList) {
      if (currentActiveTab && currentActiveTab.status != null) {
        loadReservationsByStatus(currentActiveTab.status);
      }
      const nextState = { ...location.state };
      delete nextState.refreshList;
      navigate(location.pathname + location.search, { replace: true, state: nextState });
    }
  }, [location.state?.refreshList, loadReservationsByStatus, navigate, location.pathname, location.search, currentActiveTab]);

  // 유저 정보 조회
  useEffect(() => {
    if (user) {
      apiGet('/member/getInfo.php').then(res => {
        if (res.result === 'success') {
          setEditInfoData({
            id: res.data.id,
            name: res.data.user_name,
            birth: res.data.birthday,
            gender: res.data.sex,
            tel: res.data.phone,
            requiredAgreements: {
              required1: res.data.agree01 === 'Y',
              required2: res.data.agree02 === 'Y',
              required3: res.data.agree03 === 'Y',
              required4: res.data.agree04 === 'Y',
            },
            optionalAgreements: {
              optional1: res.data.agree05 === 'Y',
              optional2: res.data.rcv_email === 'Y',
            }
          });
        }
      });
    }
  }, [user?.id]);

  // 로딩 중이거나 로그인 안 되어 있으면 렌더링 안함
  if (loading || !user) return null;

  // const renderRoutes = () => (
  //   <Routes>
  //     <Route path="/" element={<Navigate to="/mypage/reservation-request" replace />} />
  //     {allTabItems.map(tab => {
  //       const isEditProfile = tab.id === 'edit-profile';
  //       return (
  //         <Route
  //           key={tab.id}
  //           path={tab.path.replace('/mypage/', '')}
  //           element={isEditProfile ? <EditInfo data={editInfoData} /> : <tab.component data={tab.data} />}
  //         />
  //       );
  //     })}
  //   </Routes>
  // );

  const renderRoutes = () => (
    <Routes>
      <Route path="/" element={<Navigate to="/mypage/reservation-complete" replace />} />
      {allTabItems.map(tab => {
        const TabComponent = tab.component;

        const element = (() => {
          if (tab.id === 'edit-profile') {
            return <EditInfo data={editInfoData} />;
          }

          if (tab.id === 'change-password') {
            return <ChangePwd />;
          }

          if (TabComponent === MypageList) {
            const statusKey = tab.status != null ? String(tab.status) : 'all';
            const filteredData = reservationsByStatus[statusKey] || [];
            const isLoading = !!reservationsLoadingMap[statusKey];
            const error = reservationsErrorMap[statusKey] || null;
            return (
              <MypageList
                key={tab.id}
                data={filteredData}
                isLoading={isLoading}
                error={error}
                statusLabelOverride={tab.statusLabelOverride}
                buttonLabelOverride={tab.buttonLabelOverride}
                ticketIssueFilter={tab.ticketIssueFilter}
              />
            );
          }

          return <TabComponent />;
        })();

        return (
          <Route
            key={tab.id}
            path={tab.path.replace('/mypage/', '')}
            element={element}
          >
            <Route path="detail" element={<MypageDetail />} />
          </Route>
        );
      })}
    </Routes>
  );

  return (
    <section className="mypage-section">
      {!isMobile ? (
        <>
          <div className="mypage-content-side-tab">
            <SideTab name={user.user_name} id={user.id} tabs={sideTabGroupsWithCounts} />
          </div>
          <div className="mypage-content-wrapper">
            <h2 className="mypage-route">
              {currentActiveTab && (
                <>
                  <span>{sideTabGroups.find(group => group.items.includes(currentActiveTab)).groupTitle}</span>
                  <ArrowRight style={{ rotate: '180deg', width: '8px', height: '14px' }} />
                  <span>{currentActiveTab.name}</span>
                </>
              )}
            </h2>
            <div className={`mypage-content ${isDetailPage ? 'is-detail-page' : ''}`}>{renderRoutes()}</div>   
          </div>
        </>
      ) : (
        <>
          {isMypageBaseRoute ? (
            <SideTab name={user.user_name} id={user.id} tabs={sideTabGroupsWithCounts} />
          ) : (
            <div className="mypage-content-wrapper">
              <h2 className="mypage-route">
                {currentActiveTab && (
                  <>
                    <span>{sideTabGroups.find(group => group.items.includes(currentActiveTab)).groupTitle}</span>
                    <ArrowRight style={{ rotate: '180deg', width: '8px', height: '14px' }} />
                    <span>{currentActiveTab.name}</span>
                  </>
                )}
              </h2>
              <div className={`mypage-content ${isDetailPage ? 'is-detail-page' : ''}`}>{renderRoutes()}</div>   
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default Mypage;
